// Framework-free helpers for the JSON tool: structured error parsing (line/column)
// plus JSON -> CSV and JSON -> XML converters. Kept dependency-free so it is
// trivially testable and reusable.

// Parse a JSON string and, on failure, return { valid:false, line, col, message }.
// JSON.parse error messages differ across browsers, so we compute line/col from
// the character position when available, and fall back gracefully.
export function parseJsonWithError(text) {
    try {
        const data = JSON.parse(text)
        return { valid: true, data }
    } catch (err) {
        const message = cleanMessage(err.message)
        const pos = extractPosition(err.message)
        if (pos != null) {
            const { line, col } = posToLineCol(text, pos)
            return { valid: false, line, col, message }
        }
        return { valid: false, message }
    }
}

function cleanMessage(msg = '') {
    return String(msg)
        .replace(/\s+in JSON at position \d+.*/i, '')
        .replace(/\s+at position \d+.*/i, '')
        .replace(/\s+\(line \d+ column \d+.*\)/i, '')
        .replace(/^JSON\.parse:\s*/i, '')
        .trim()
}

function extractPosition(msg = '') {
    const m = /position (\d+)/i.exec(msg)
    if (m) return parseInt(m[1], 10)
    return null
}

function posToLineCol(text, pos) {
    const upto = text.slice(0, pos)
    const lines = upto.split('\n')
    const line = lines.length
    const col = lines[lines.length - 1].length + 1
    return { line, col }
}

// ---- JSON -> CSV -------------------------------------------------------------
// Accepts an array of objects (most common API shape) OR a single object.
// Flattens nested keys with dot notation (e.g. address.city).
export function jsonToCsv(data) {
    let rows = Array.isArray(data) ? data : [data]
    if (rows.length === 0) return ''

    const flatRows = rows.map(function (r) { return flatten(r) })
    const columns = []
    const seen = new Set()
    for (const row of flatRows) {
        for (const key of Object.keys(row)) {
            if (!seen.has(key)) { seen.add(key); columns.push(key) }
        }
    }

    const escapeCell = (val) => {
        if (val === null || val === undefined) return ''
        const s = typeof val === 'object' ? JSON.stringify(val) : String(val)
        if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"'
        return s
    }

    const header = columns.map(escapeCell).join(',')
    const body = flatRows
        .map(row => columns.map(col => escapeCell(row[col])).join(','))
        .join('\n')
    return header + '\n' + body
}

function flatten(obj, prefix = '', out = {}) {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
        out[prefix || 'value'] = obj
        return out
    }
    for (const [key, val] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            flatten(val, path, out)
        } else {
            out[path] = val
        }
    }
    return out
}

// ---- JSON -> XML -------------------------------------------------------------
export function jsonToXml(data, rootName = 'root') {
    const body = buildXml(data, '  ')
    return `<?xml version="1.0" encoding="UTF-8"?>\n<${rootName}>\n${body}</${rootName}>\n`
}

function escapeXml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

function xmlSafeTag(name) {
    let t = String(name).replace(/[^a-zA-Z0-9_.-]/g, '_')
    if (/^[^a-zA-Z_]/.test(t)) t = '_' + t
    return t
}

function buildXml(value, indent) {
    if (value === null || value === undefined) return ''
    if (Array.isArray(value)) {
        return value.map(item => `${indent}<item>${inlineOrBlock(item, indent)}</item>\n`).join('')
    }
    if (typeof value === 'object') {
        let out = ''
        for (const [key, val] of Object.entries(value)) {
            const tag = xmlSafeTag(key)
            out += `${indent}<${tag}>${inlineOrBlock(val, indent)}</${tag}>\n`
        }
        return out
    }
    return escapeXml(value)
}

function inlineOrBlock(val, indent) {
    if (val !== null && typeof val === 'object') {
        return '\n' + buildXml(val, indent + '  ') + indent
    }
    if (val === null || val === undefined) return ''
    return escapeXml(val)
}